import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";


export enum UniversalDialogType {
  Confirm = "Confirm",
  Input = "Input",
  Alert = "Alert",
  List = "List",
  Progress = "Progress",
}

export interface UniversalDialogProps {
  title: string;
  text: string;
  type: UniversalDialogType;
  defaultInputValue?: string;
  onComplete: (yes: boolean, inputValue: string) => void;
  openDialog: boolean;
  progress?: number;
  setProgress?: (progress: number) => void;
}

let _setUniversalDialog: React.Dispatch<React.SetStateAction<UniversalDialogProps>>;
export function initUniversalDialog(
  setUniversalDialog: React.Dispatch<React.SetStateAction<UniversalDialogProps>>
) {
  _setUniversalDialog = setUniversalDialog;
}

let _setProgress: React.Dispatch<React.SetStateAction<number>> | null = null;

export function initProgressSetter(setter: React.Dispatch<React.SetStateAction<number>>) {
  _setProgress = setter;
}


export function showUniversalDialog(
  title: string,
  text: string,
  type: UniversalDialogType,
  onComplete: (yes: boolean, inputValue: string) => void,
  defaultInputValue = ""
) {
  _setUniversalDialog({
    title,
    text,
    openDialog: true,
    type,
    defaultInputValue,
    onComplete: (yes, inputValue) => {
      onComplete(yes, inputValue);
      _setUniversalDialog({
        title: "",
        text: "",
        openDialog: false,
        type,
        onComplete: () => { },
        defaultInputValue,
      });
    },
  });
}

export function showConfirmDialog(
  title: string,
  text: string
): Promise<boolean> {
  return new Promise((resolve) => {
    showUniversalDialog(
      title,
      text,
      UniversalDialogType.Confirm,
      (yes, inputValue) => {
        resolve(yes);
      }
    );
  });
}

export interface ProgressDialogHandle {
  updateText: (newText: string) => void;
  setProgress: (value: number | ((prev: number) => number)) => void;
  onComplete: (yes: boolean, inputValue: string) => void;
}

// Store the current dialog's onComplete to avoid overwriting
let currentProgressDialog: { onComplete: (yes: boolean, inputValue: string) => void } | null = null;

export function showProgressDialog(
  title: string,
  text: string,
  initialProgress: number = 0
): Promise<void> & ProgressDialogHandle {
  return Object.assign(
    new Promise<void>((resolve) => {
      const closeDialog = (yes: boolean, inputValue: string) => {
        _setUniversalDialog({
          title: "",
          text: "",
          openDialog: false,
          type: UniversalDialogType.Progress,
          progress: 0,
          onComplete: () => { },
        });
        if (_setProgress) {
          _setProgress(0);
        }
        currentProgressDialog = null; // Clear the current dialog
        resolve();
      };

      if (_setProgress) {
        console.log("Setting initial progress:", initialProgress);
        _setProgress(initialProgress);
      }

      currentProgressDialog = { onComplete: closeDialog };

      _setUniversalDialog({
        title,
        text,
        openDialog: true,
        type: UniversalDialogType.Progress,
        progress: initialProgress,
        onComplete: closeDialog,
      });
    }),
    {
      updateText: (newText: string) => {
        _setUniversalDialog((prev: UniversalDialogProps) => {
          if (prev.type === UniversalDialogType.Progress && prev.openDialog) {
            return { ...prev, text: newText };
          }
          return prev;
        });
      },
      setProgress: (value: number | ((prev: number) => number)) => {
        if (_setProgress) {
          console.log("Updating progress to:", value);
          _setProgress(value);
        }
      },
      onComplete: (yes: boolean, inputValue: string) => {
        if (currentProgressDialog) {
          currentProgressDialog.onComplete(yes, inputValue);
        }
      },
    }
  );
}


export function showInputDialog(title: string, text: string, defaultInputValue = ''): Promise<string> {
  return new Promise((resolve) => {
    showUniversalDialog(
      title,
      text,
      UniversalDialogType.Input,
      (yes, inputValue) => {
        if (yes) {
          resolve(inputValue);
        } else {
          resolve("");
        }
      },
      defaultInputValue
    );
  });
}



export function showListDialog(title: string, message: string): Promise<void> {
  return new Promise((resolve) => {
    showUniversalDialog(
      title,
      "",
      UniversalDialogType.List,
      (yes, inputValue) => resolve(),
      ""
    );
  });
}


export default function UniversalDialog({
  title,
  text,
  onComplete,
  type,
  defaultInputValue,
  openDialog,
  progress,
  setProgress, // need this to update the progress bar
}: UniversalDialogProps) {
  const [open, setOpen] = useState(openDialog);
  const [inputValue, setInputValue] = useState(defaultInputValue || "");
  const [searchText, setSearchText] = useState("");
  const [selectedMap, setSelectedMap] = useState("");

  // console.log({ defaultInputValue });

  useEffect(() => {
    setOpen(openDialog);
    if (
      openDialog &&
      type === UniversalDialogType.Input &&
      defaultInputValue &&
      !defaultInputValue.includes(",")
    ) {
      setInputValue(defaultInputValue);
    }
  }, [openDialog, type, defaultInputValue]);


  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && open) {
        event.preventDefault();
        onYesClicked();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, inputValue]);

  function handleClose() {
    onComplete(false, "");
    setOpen(false);
  }

  function onYesClicked() {
    onComplete(true, inputValue);
    setOpen(false);
  }

  const buttonStyles = {
    color: "whitesmoke",
    border: "1px solid whitesmoke",
    borderRadius: "8px",
    marginRight: "15px",
    marginLeft: "15px",
    marginBottom: "8px",
    padding: "4px 16px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  };

  const dialogActionsStyles = {
    justifyContent: "space-between",
    backgroundColor: "#171717",
    paddingTop: '1rem',
  };

  const filteredMapList = text.split('\n').filter(name => name.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <Dialog
      open={open}
      onClose={type !== UniversalDialogType.Progress ? handleClose : undefined}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: "whitesmoke",
          width: "600px",
          maxWidth: "90%",
          boxShadow: "none",
          border: "1px solid #444",
        },
      }}
    >
      <DialogTitle
        id="alert-dialog-title"
        style={{ backgroundColor: "#171717" }}
      >
        {title}
      </DialogTitle>

      <DialogContent style={{ backgroundColor: "#171717" }}>
        {type === UniversalDialogType.Confirm &&
          (
            <Typography variant="h6">{text}</Typography>
          )}

        {type === UniversalDialogType.Progress && (
          <div style={{ width: '100%' }}>
            <Typography
              component="h6"
              style={{ marginBottom: "1rem", textAlign: "center", color: "whitesmoke" }}
            >
              {text}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "white",
                },
                backgroundColor: "gray",
                height: 10,
                borderRadius: 5,
              }}
            />
          </div>
        )}

        {type === UniversalDialogType.Alert && (
          <Typography variant="h5">{text}</Typography>
        )}

        {type === UniversalDialogType.Input && (
          (defaultInputValue ?? '').includes(',') ? (
            <TextField
              select
              label="Select an option"
              fullWidth
              value={inputValue}

              onChange={(e) => setInputValue(e.target.value)}
              sx={{
                mt: 2,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "gray",
                  },
                  "&:hover fieldset": {
                    borderColor: "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#9e9e9e",
                },
                "& .Mui-focused .MuiInputLabel-root": {
                  color: "white",
                },
              }}
            >
              {(defaultInputValue || '').split(',').map((option) => (
                <MenuItem key={option.trim()} value={option.trim()}>
                  {option.trim()}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              autoFocus
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "gray",
                  },
                  "&:hover fieldset": {
                    borderColor: "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#9e9e9e",
                },
                "& .Mui-focused .MuiInputLabel-root": {
                  color: "white",
                },
              }}
            />
          )
        )}
      </DialogContent>

      {type === UniversalDialogType.Alert && (
        <DialogActions
          style={{ backgroundColor: "#171717" }}
          sx={dialogActionsStyles}
        >
          <Button onClick={onYesClicked} sx={buttonStyles} autoFocus>
            Ok
          </Button>
        </DialogActions>
      )}
      {type === UniversalDialogType.Confirm && (
        <DialogActions
          sx={{ ...dialogActionsStyles, backgroundColor: "#171717" }}
        >

          <Button onClick={handleClose} sx={buttonStyles} autoFocus>
            No
          </Button>
          <Button onClick={onYesClicked} sx={buttonStyles} autoFocus>
            Yes
          </Button>
        </DialogActions>
      )}

      {type === UniversalDialogType.Input && (
        <DialogActions sx={dialogActionsStyles}>

          <Button onClick={handleClose} sx={buttonStyles} autoFocus>
            Cancel
          </Button>
          <Button onClick={onYesClicked} sx={buttonStyles} autoFocus>
            Ok
          </Button>
        </DialogActions>
      )}

      {type === UniversalDialogType.List && (
        <>
          <TextField
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            autoFocus
            sx={{
              backgroundColor: "#171717",
              paddingBottom: '1rem',
              paddingRight: '24px',
              paddingLeft: '24px',
              fontSize: '13px',
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  searchText && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchText("")}
                        sx={{
                          color: "whitesmoke",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <Close />
                      </IconButton>

                    </InputAdornment>
                  )
                )
              },
            }}
          />
          <Box sx={{
            backgroundColor: "#171717",
            margin: 0,
            padding: "0 1.5rem",
            width: "100%",
          }}>
            <DialogContent
              sx={{
                backgroundColor: "#171717",
                height: 350,
                border: '1.5px solid whitesmoke',
                borderRadius: '3px',
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                padding: 0,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#111",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#444",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#666",
                },
              }}
            >
              {filteredMapList.map((name, mapName) => (
                <Button
                  key={mapName}
                  fullWidth
                  onClick={() => setSelectedMap(name)}
                  onDoubleClick={() => {
                    setSelectedMap(name); 
                    onComplete(true, name);
                    setOpen(false);
                  }}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: '15px',
                    fontWeight: "400",
                    paddingLeft: "1rem",
                    backgroundColor: '171717',
                    "&:hover": {
                      backgroundColor: "#444",
                    },
                    "&:active": {
                      backgroundColor: "#666",
                    },
                    "&:focus": {
                      backgroundColor: "#666",
                    },
                    color: "whitesmoke",
                    borderBottom: "1px solid #444",
                    borderRadius: 0,
                  }}
                >
                  {name}
                </Button>
              ))}
              {filteredMapList.length === 0 && (
                <Typography
                  sx={{
                    color: "#888",
                    fontSize: "15px",
                    textAlign: "center",
                    padding: "1rem",
                  }}
                >
                  No maps found
                </Typography>
              )}

            </DialogContent>
          </Box>


          <DialogActions
            sx={{
              ...dialogActionsStyles,
            }}
          >
            <Button onClick={handleClose} sx={buttonStyles}>
              Cancel
            </Button>
            <Button
              sx={buttonStyles}
              onClick={() => {
                onComplete(true, selectedMap);
                setOpen(false);
              }}>
              OK
            </Button>
          </DialogActions>
        </>
      )}

    </Dialog>
  );
}

export function AlertDialog({
  title,
  text,
  openDialog,
  onComplete,
}: UniversalDialogProps) {
  const [open, setOpen] = useState(openDialog);
  useEffect(() => {
    if (openDialog) {
      setOpen(true);
    }
  }, [openDialog]);
  function handleClose() {
    onComplete(false, "");
    setOpen(false);
  }
  function onYesClicked() {
    onComplete(true, "");
    setOpen(false);
  }
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onYesClicked} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function LicenseDialog({
  title,
  text,
  openDialog,
  onComplete,
}: UniversalDialogProps) {
  const [open, setOpen] = useState(openDialog);
  useEffect(() => {
    if (openDialog) {
      setOpen(true);
    }
  }, [openDialog]);
  function handleClose(reason: any) {
    onComplete(false, "");
    setOpen(false);
  }
  function onYesClicked() {
    onComplete(true, "");
    setOpen(false);
  }
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent>{text}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          Decline
        </Button>
        <Button onClick={onYesClicked} color="primary" autoFocus>
          Accept
        </Button>

      </DialogActions>
    </Dialog>
  );
}


interface SnackbarProps {
  message: string;
  severity: "success" | "error" | "warning" | "info";
  openSnack: boolean;
  autoHideDuration?: number;
  vertical?: "top" | "bottom";
  horizontal?: "left" | "center" | "right";
  onClose?: () => void;
}


export function SnackbarComponent({
  message,
  severity,
  openSnack,
  autoHideDuration = 6000,
  vertical = "top",
  horizontal = "center",
  onClose,
}: SnackbarProps) {
  const [open, setOpen] = useState(openSnack);


  useEffect(() => {
    setOpen(openSnack);
  }, [openSnack]);

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical, horizontal }}
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
    >
      <Alert severity={severity} onClose={handleClose}>
        {message}
      </Alert>
    </Snackbar>
  );
}


let _setSnackMessage: React.Dispatch<React.SetStateAction<string>> | null = null;
let _setSnackSeverity: React.Dispatch<React.SetStateAction<"success" | "error" | "warning" | "info">> | null = null;

export function initSnackbar(
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  setSeverity: React.Dispatch<React.SetStateAction<"success" | "error" | "warning" | "info">>
) {
  _setSnackMessage = setMessage;
  _setSnackSeverity = setSeverity;
}

export function showSnackbar(
  message: string,
  severity: "success" | "error" | "warning" | "info" = "info"
) {
  if (_setSnackMessage && _setSnackSeverity) {
    _setSnackSeverity(severity);
    _setSnackMessage(message);
  } else {
    console.warn("Snackbar not initialized");
  }
}
